import { formatJobDates, groupJobsByShipDate } from './tableUtils';
import { sortJobsBySoftwareDue } from './softwareViewUtils';
import { sortJobsByControlPanelDue } from './controlPanelViewUtils';
import { sortJobsByNorthPrairieDue } from './northPrairieViewUtils';
import { getProjectStatus } from '../../../utils/dataNormalization';

// Function to determine if an item has long build pumps
export const hasLongBuildPumps = (job, pumpDetails) => {
  if (!job || !pumpDetails || !Array.isArray(pumpDetails)) {
    return { hasLongBuild: false, tplValue: null };
  }

  // Find pump details that match the job's ID
  const matchingPumps = pumpDetails.filter(pump => 
    pump.ProjectID && 
    job.ID && 
    pump.ProjectID.toString() === job.ID.toString()
  );

  // Check if any matching pump meets the long build criteria
  for (const pump of matchingPumps) {
    const isVTType = pump.PumpType && pump.PumpType.toUpperCase() === 'VT';
    const isMainPump = pump.PumpID && pump.PumpID.toLowerCase() === 'main';
    const tplValue = pump.PumpTPL ? parseFloat(pump.PumpTPL) : 0;
    const isLongTPL = tplValue > 237;

    if (isVTType && isMainPump && isLongTPL) {
      return { hasLongBuild: true, tplValue: tplValue };
    }
  }

  return { hasLongBuild: false, tplValue: null };
};

export const createDataProcessingFunctions = (
  jobScheduleData,
  debouncedSearchTerm,
  weekColumns,
  weeksToShow,
  isSearchActive = false, // Add parameter to indicate if search is active
  softwareView = false, // Add parameter for software view
  controlPanelView = false,
  northPrairieView = false
) => {
  // Filter and format data
  const formattedJobs = jobScheduleData ? formatJobDates(jobScheduleData) : [];
  
  // Define allowed statuses
  const allowedStatuses = [
    "Full Release",
    "Engineering Only",
    "Long Lead Only",
    "Release with Qualified Exceptions",
    "Release Pending CSO",
    "Release with CSO Pending",
    "Release w/ CSO Pending"
  ];
  
  // First filter by allowed statuses
  let processedJobs = formattedJobs.filter(job => allowedStatuses.includes(getProjectStatus(job)));
  
  // Apply search filter if there's a search term
  if (debouncedSearchTerm) {
    processedJobs = processedJobs.filter(job => 
      (job.JobNumber && job.JobNumber.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
      (job.ProjectName && job.ProjectName.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
      (job.ModelNumber && job.ModelNumber.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
    );
  }
  
  // Apply software view sorting if enabled
  if (softwareView) {
    processedJobs = sortJobsBySoftwareDue(processedJobs);
  } else if (controlPanelView) {
    processedJobs = sortJobsByControlPanelDue(processedJobs);
  } else if (northPrairieView) {
    processedJobs = sortJobsByNorthPrairieDue(processedJobs);
  }
  
  // Group all jobs by ship date first
  const allGroupedJobs = groupJobsByShipDate(processedJobs, weekColumns);
  
  // Get all weeks for sorting
  const allWeeks = Object.keys(allGroupedJobs);
  
  // Separate and sort scheduled weeks
  const scheduledWeeks = allWeeks
    .filter(week => week !== "Unscheduled" && week !== "Past Due")
    .sort((a, b) => {
      const weekNumA = parseInt(a.replace("Week ", ""), 10);
      const weekNumB = parseInt(b.replace("Week ", ""), 10);
      return weekNumA - weekNumB;
    });
  
  let sortedWeeks;
  let groupedJobs;
  
  if (isSearchActive && debouncedSearchTerm) {
    // When search is active, show all weeks that have matching jobs
    sortedWeeks = scheduledWeeks;
    groupedJobs = allGroupedJobs;
  } else {
    // When no search or search is empty, apply week filter
    const visibleWeeks = weeksToShow === 999 ? scheduledWeeks : scheduledWeeks.slice(0, weeksToShow);
    sortedWeeks = visibleWeeks;
    
    // Filter grouped jobs to only include visible weeks
    groupedJobs = {};
    visibleWeeks.forEach(week => {
      if (allGroupedJobs[week]) {
        groupedJobs[week] = allGroupedJobs[week];
      }
    });
    

  }
  
  // Always add Past Due at the beginning if it has items
  if (allGroupedJobs["Past Due"] && allGroupedJobs["Past Due"].length > 0) {
    groupedJobs["Past Due"] = allGroupedJobs["Past Due"];
    sortedWeeks.unshift("Past Due");
  }
  
  return {
    formattedJobs: processedJobs,
    searchFilteredJobs: processedJobs,
    groupedJobs,
    sortedWeeks
  };
};
